import React, { useState, useEffect, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { loadDecisionTypes } from 'actions/actionCreators/dictionariesActions';
import {
  loadApprovalLevels,
  updateApprovalLevel,
  applyApprovalLevel,
  loadApprovalsHistory,
  invalidateApprovalsHistory,
  loadApprovalTypeDecisionReasons
} from '../actionCreators';
import { loadFinancedOffers } from 'features/folder/edit/generalData/financedOffers/actionCreators';
import { loadUserGroups } from 'features/userProfile/userGroups/actionCreators';
import { getApprovalLevels, getDecisionTypes, getSelectedOffer, getApprovalsHistory, getUserProfile } from '../selectors';
import { isInRange } from 'utils/simple-validations';
import { when, valueChanged } from 'utils/fn-combinators';
import * as editableDocument from 'utils/model/editableDocument';
import { collection } from 'utils/immutable';
import flow from 'lodash.flow';
import ApprovalLevelsCard from './ApprovalLevelsCard';
import ApprovalsHistoryModal from './ApprovalsHistoryModal';
import { ParamsContext } from 'providers/ParamsContext';
import { round } from 'lodash';
import { useQueryWithErrorHandling } from 'customHooks/errorHandling';
import { USER_PROFILE_QUERY } from 'features/userProfile/queries';

const ApprovalLevelsContainer = ({ documentId, siteId }) => {
  const [applying, setApplying] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [localApprovalLevels, setLocalApprovalLevels] = useState(null);
  const [queueSaveForIndex, setQueueSaveForIndex] = useState(null);
  const dispatch = useDispatch();
  const { cvsChAmount } = useContext(ParamsContext);
  const key = `${documentId}-${siteId}`;
  const { approvalLevels, loading, loaded } = useSelector(state => getApprovalLevels(state, key), shallowEqual);
  const { approvalsHistory, loading: approvalsHistoryLoading } = useSelector(state => getApprovalsHistory(state, key), shallowEqual);
  const decisionTypes = useSelector(state => getDecisionTypes(state), shallowEqual);
  const userProfile = useSelector(state => getUserProfile(state), shallowEqual);
  const decisionReasons = useSelector(state => state.document.decision.approvalTypeDecisionReasons, shallowEqual);
  const selectedOffer = useSelector(state => getSelectedOffer(state), shallowEqual);

  const approvalTypeId = loaded && approvalLevels[0]?.approvalTypeId;

  const { data: currentUserInfo } = useQueryWithErrorHandling(USER_PROFILE_QUERY);

  useEffect(() => {
    dispatch(loadApprovalLevels(documentId, siteId));
    dispatch(loadFinancedOffers(documentId, siteId));
    dispatch(loadDecisionTypes());
    dispatch(loadUserGroups());
  }, [dispatch, documentId, siteId]);

  useEffect(() => {
    if ((loaded && localApprovalLevels === null) || (loaded && localApprovalLevels?.some(x => x.document?.shouldRefresh))) {
      setLocalApprovalLevels(approvalLevels.map(item => editableDocument.create({ ...item, saving: false, applying: false })));
    } else dispatch(loadApprovalLevels(documentId, siteId));
  }, [documentId, siteId, approvalLevels, localApprovalLevels, loaded, dispatch]);

  useEffect(() => {
    if (loaded) {
      if (approvalTypeId !== null && approvalTypeId !== undefined) dispatch(loadApprovalTypeDecisionReasons(approvalTypeId));
    }
  }, [approvalLevels, dispatch, loaded, approvalTypeId]);

  useEffect(() => {
    if (showModal) dispatch(loadApprovalsHistory(documentId, siteId));
    else dispatch(invalidateApprovalsHistory(documentId, siteId));
  }, [showModal, documentId, siteId, dispatch]);

  const getItemValidationRules = () => ({
    advancePercent: [isInRange(0, 1000)],
    durationMonths: [isInRange(0, 1000)]
  });

  const getItemBusinessRules = useCallback(
    () => ({
      changedByName: when(
        valueChanged(level => level.decisionType),
        currentUserInfo?.userInfo?.lastName
          ? `${currentUserInfo.userInfo.firstName} ${currentUserInfo.userInfo.lastName}`
          : userProfile.name
      ),
      decisionDate: when(
        valueChanged(level => level.decisionType),
        new Date()
      ),
      financingValuePercent: level => 100 - level.advancePercent,
      financingValue: level => round((level.documentAmount * level.financingValuePercent) / 100, cvsChAmount)
    }),
    [currentUserInfo, userProfile, cvsChAmount]
  );

  const changeItemProperty = useCallback(
    (index, prop, value) => {
      setLocalApprovalLevels(prevState =>
        collection.update(
          index,
          flow(editableDocument.update(prop, value, getItemBusinessRules()), editableDocument.validate(getItemValidationRules(), true)),
          prevState
        )
      );
    },
    [getItemBusinessRules]
  );

  const saveItem = useCallback(
    idx => {
      setLocalApprovalLevels(prevState =>
        collection.update(idx, editableDocument.update('saving', true, getItemBusinessRules()), prevState)
      );
      dispatch(
        updateApprovalLevel(
          documentId,
          siteId,
          localApprovalLevels[idx].document,
          localApprovalLevels[idx].dirtyInfo.requestedApprovalUserId,
          _ => {
            setLocalApprovalLevels(prevState =>
              collection.update(
                idx,
                flow(editableDocument.update('shouldRefresh', true, getItemBusinessRules()), editableDocument.resetDirtyInfo),
                prevState
              )
            );
          }
        )
      );
    },
    [dispatch, documentId, siteId, localApprovalLevels, getItemBusinessRules]
  );

  const applyItem = useCallback(
    idx => {
      const item = editableDocument.validate(getItemValidationRules(), null, localApprovalLevels[idx]);

      if (!item.validationInfo.isValid) {
        setLocalApprovalLevels(prevState => collection.update(idx, item, prevState));
        return;
      }

      setLocalApprovalLevels(prevState =>
        collection.update(idx, editableDocument.update('applying', true, getItemBusinessRules()), prevState)
      );
      setApplying(true);

      dispatch(
        applyApprovalLevel(documentId, siteId, localApprovalLevels[idx].document, () => {
          setApplying(false);
          setLocalApprovalLevels(prevState =>
            collection.update(
              idx,
              flow(editableDocument.update('applying', false, getItemBusinessRules()), editableDocument.resetDirtyInfo),
              prevState
            )
          );
        })
      );
    },
    [dispatch, documentId, getItemBusinessRules, localApprovalLevels, siteId]
  );

  const showApprovalHistory = useCallback(() => setShowModal(true), []);
  const onCloseApprovalHistoryModal = useCallback(() => setShowModal(false), []);

  useEffect(() => {
    if (queueSaveForIndex != null) {
      saveItem(queueSaveForIndex);
      setQueueSaveForIndex(null);
    }
  }, [queueSaveForIndex, saveItem, setQueueSaveForIndex]);

  return (
    <>
      {localApprovalLevels && (
        <ApprovalLevelsCard
          approvalLevels={localApprovalLevels}
          decisionTypes={decisionTypes}
          applying={applying}
          loading={loading}
          changeItemProperty={changeItemProperty}
          saveItem={saveItem}
          applyItem={applyItem}
          showApprovalHistory={showApprovalHistory}
          setQueueSaveForIndex={setQueueSaveForIndex}
          currentUserId={currentUserInfo?.userInfo?.userId}
          selectedOffer={selectedOffer}
          decisionReasons={
            decisionReasons && decisionReasons[approvalTypeId] && decisionReasons[approvalTypeId].loaded
              ? decisionReasons[approvalTypeId].decisionReasons
              : []
          }
        />
      )}
      {approvalsHistory && (
        <ApprovalsHistoryModal
          showModal={showModal}
          onCloseModal={onCloseApprovalHistoryModal}
          approvalsHistory={approvalsHistory}
          approvalsHistoryLoading={approvalsHistoryLoading}
        />
      )}
    </>
  );
};

ApprovalLevelsContainer.propTypes = {
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired
};

export default ApprovalLevelsContainer;
